import React, { useState, useEffect } from 'react';
import APIService from '../services/api';

/**
 * Onglet « Administratif société » d'un dossier AO — réservé à l'administrateur.
 *
 * Pièces communes à tous les lots (jamais rattachées à un candidat), suivi de
 * validité, extraction des exigences du cahier des charges, et arborescence
 * kDrive proposée puis créée sur validation explicite.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

export const isAoAdmin = (u) => !!(u?.admin === true || u?.role === 'admin');

// Jeu de pièces suisses de référence, utilisé tant que rien n'est configuré.
export const DEFAULT_SOCIETE_DOCS = [
  { nom: 'Extrait du Registre du commerce', source: 'Registre du commerce', obligatoire: true, issued: null, validite_mois: 3 },
  { nom: 'Attestation Office des poursuites', source: 'Office des poursuites', obligatoire: true, issued: null, validite_mois: 3 },
  { nom: 'Attestation impôt à la source', source: 'Administration fiscale', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'Attestation AVS/AI/APG', source: 'Caisse de compensation', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'Attestation LPP', source: 'Institution de prévoyance', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'Attestation TVA', source: 'AFC', obligatoire: false, issued: null, validite_mois: 12 },
  { nom: 'Attestation SUVA / assurance accidents', source: 'SUVA', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'RC professionnelle', source: 'Assureur', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'Attestation CCT / usages OCIRT', source: 'OCIRT', obligatoire: true, issued: null, validite_mois: 12 },
  { nom: 'Déclaration d\'intégrité signée', source: 'Interne — propre à l\'AO', obligatoire: true, issued: null, validite_mois: 0, scope: 'ao' },
];

// ── Dates & échéances ──────────────────────────────────────────────────────
const DAY = 86400000;
const parseDate = (s) => { if (!s) return null; const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d; };
const fmtDate = (d) => d ? d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.') : '';
const addMonths = (d, m) => { const x = new Date(d.getTime()); x.setMonth(x.getMonth() + m); return x; };
const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

/**
 * État d'échéance d'une pièce. Le libellé secondaire suit `issued` (fournie ou
 * non), jamais `expire` : une pièce fournie sans échéance n'est pas « à fournir ».
 */
export const docState = (doc) => {
  const issued = parseDate(doc.issued);
  const months = Number(doc.validite_mois) || 0;
  if (!issued) return { key: 'missing', label: 'Manquant', tone: 'danger', sub: 'À fournir', ok: false };
  if (months === 0) return { key: 'none', label: 'Sans échéance', tone: 'ok', sub: `Fournie le ${fmtDate(issued)} · sans échéance`, ok: true };
  const expire = addMonths(issued, months);
  const days = Math.round((expire - today0()) / DAY);
  const sub = `Échéance ${fmtDate(expire)}`;
  if (days < 0) return { key: 'expired', label: `Expiré depuis ${Math.abs(days)} j`, tone: 'danger', sub, ok: false };
  if (days <= 30) return { key: 'soon', label: `Expire dans ${days} j`, tone: 'warn', sub, ok: true };
  return { key: 'valid', label: `Valide — ${days} j`, tone: 'ok', sub, ok: true };
};

/** Nombre de pièces obligatoires non conformes — sert au badge de l'onglet. */
export const societeAlerts = (docs) => (docs || []).filter(d => d.obligatoire && !docState(d).ok).length;

const TONE = {
  ok: { bg: 'rgba(45,200,110,0.15)', fg: '#16a34a' },
  warn: { bg: 'rgba(217,119,6,0.14)', fg: '#d97706' },
  danger: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
};

// ── Arborescence ───────────────────────────────────────────────────────────
export const slug = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
/** « David PACHE » → « PACHE_David » : les parties en capitales sont le nom. */
export const candidateFolder = (raw) => {
  const parts = (raw || '').split(/[\s_]+/).filter(Boolean);
  const last = parts.filter(p => p === p.toUpperCase() && /[A-ZÀ-Ý]/.test(p));
  const first = parts.filter(p => !last.includes(p));
  const ordered = last.length && first.length ? [...last, ...first] : parts;
  return slug(ordered.join('_'));
};

const mono = { fontFamily: "'IBM Plex Mono',monospace" };
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 13px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' };
const btnMain = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontWeight: 700 };
const chip = (bg, fg) => ({ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: bg, color: fg, whiteSpace: 'nowrap' });

export default function AoSociete({ aoId, aoMeta, report, showNotification, docs, onPersist, onDocsFromServer, saving, storeUnavailable }) {
  const authHeaders = { Authorization: `Bearer ${APIService.getToken()}` };
  const jsonHeaders = { ...authHeaders, 'Content-Type': 'application/json' };

  const [cdc, setCdc] = useState(null);           // exigences extraites
  const [cdcDocs, setCdcDocs] = useState([]);     // documents importés
  const [scanning, setScanning] = useState(false);
  const [kdrive, setKdrive] = useState({ linked: false, root: null });
  const [linking, setLinking] = useState(false);
  const [preview, setPreview] = useState(null);   // arbre proposé, non écrit
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [events, setEvents] = useState([]);

  // Journal : le back crée/supprime seul les dossiers NOM_Prénom au
  // positionnement et au retrait — le front n'a qu'à relire les événements.
  const loadEvents = async () => {
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/kdrive/events?limit=3`, { headers: authHeaders });
      if (!r.ok) return;
      const j = await r.json().catch(() => ({}));
      const list = j.events || j.data?.events || (Array.isArray(j.data) ? j.data : []);
      if (Array.isArray(list)) setEvents(list);
    } catch {}
  };

  useEffect(() => {
    if (report?.kdrive?.url || report?.kdrive?.root) setKdrive(k => ({ ...k, linked: true, root: k.root || report.kdrive.root || report.kdrive.path || report.kdrive.url }));
  }, [report]);

  useEffect(() => {
    if (kdrive.linked) loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kdrive.linked, aoId]);

  const readBase64 = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Lecture du fichier impossible'));
    fr.onload = () => resolve(String(fr.result || '').split(',').pop());
    fr.readAsDataURL(file);
  });

  const persist = (next) => onPersist(next);

  const setValidity = (i, months) => persist(docs.map((d, k) => k === i ? { ...d, validite_mois: months } : d));

  // Import / renouvellement : on dépose le document dans 00_Administratif_societe.
  // Le serveur remplace l'ancien fichier au renouvellement et renvoie la liste
  // société à jour — inutile de relire la config.
  const docInputRef = React.useRef(null);
  const docTargetRef = React.useRef(null);
  const [uploading, setUploading] = useState(null);
  const askDocFile = (i) => { docTargetRef.current = i; docInputRef.current?.click(); };
  const uploadSocieteDoc = async (file) => {
    const i = docTargetRef.current; docTargetRef.current = null;
    if (!file || i == null) return;
    const doc = docs[i];
    setUploading(i);
    try {
      const base64 = await readBase64(file);
      const r = await fetch(`${BASE}/ao/${aoId}/societe/piece`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ nom: doc.nom, base64, original_name: file.name, mime_type: file.type || 'application/pdf', issued: new Date().toISOString().slice(0, 10) }),
      });
      if (r.status === 501) {
        // Route absente : on enregistre au moins la date de fourniture.
        persist(docs.map((d, k) => k === i ? { ...d, issued: new Date().toISOString().slice(0, 10) } : d));
        showNotification?.(`Le dépôt du fichier arrive côté serveur — « ${doc.nom} » enregistrée au ${fmtDate(today0())}`, 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      if (Array.isArray(j.docs) && j.docs.length) onDocsFromServer?.(j.docs);
      else persist(docs.map((d, k) => k === i ? { ...d, issued: j.issued || new Date().toISOString().slice(0, 10) } : d));
      showNotification?.(j.replaced > 0
        ? `« ${j.nom || doc.nom} » renouvelée — ancien fichier remplacé sur kDrive`
        : `« ${j.nom || doc.nom} » déposée dans 00_Administratif_societe`, 'success');
    } catch (e) { showNotification?.(`Dépôt impossible : ${e.message}`, 'error'); }
    finally { setUploading(null); }
  };

  const conformes = docs.filter(d => docState(d).ok).length;

  // ── Bloc 2 : scan du cahier des charges ──
  // Sans argument : détection automatique du CdC du dossier. Avec un fichier :
  // on l'envoie en base64 (le serveur accepte aussi { file_id }).
  const cdcInputRef = React.useRef(null);
  const replaceRef = React.useRef(null);   // document remplacé par le prochain import
  const onCdcFile = async (file) => { if (file) scanCdc(file); };
  const fmtSize = (n) => n == null ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`;

  const scanCdc = async (file) => {
    const replacing = replaceRef.current; replaceRef.current = null;
    // Étape visible pendant tout le traitement : lecture puis analyse.
    setScanning(file ? { phase: 'read', name: file.name } : { phase: 'scan', name: 'Document du dossier' });
    try {
      let body = '{}';
      if (file) {
        const base64 = await readBase64(file);
        setScanning({ phase: 'scan', name: file.name });
        body = JSON.stringify({ base64, original_name: file.name, mime_type: file.type || 'application/pdf' });
      }
      const r = await fetch(`${BASE}/ao/${aoId}/cdc/scan`, { method: 'POST', headers: jsonHeaders, body });
      if (r.status === 501) {
        showNotification?.('L\'analyse du cahier des charges arrive côté serveur — le suivi de validité reste utilisable.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 404 = CdC introuvable, 422 = texte illisible (PDF scanné), 502 = réponse
      // IA illisible : la raison du serveur est affichée telle quelle.
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const list = (j.exigences || j.data?.exigences || (Array.isArray(j.data) ? j.data : [])) || [];
      const key = `${Date.now()}-${(file?.name || j.source || 'cdc')}`;
      const doc = { key, nom: file?.name || j.source || j.data?.source || 'Cahier des charges', taille: file?.size ?? null, count: list.length };
      // Chaque exigence garde la trace de son document : retirer le document
      // retire proprement ce qu'il avait apporté.
      const tagged = list.map(ex => ({ ...ex, _src: key }));
      setCdcDocs(prev => (replacing ? prev.filter(d => d.key !== replacing) : prev).concat(doc));
      setCdc(prev => (replacing ? (prev || []).filter(ex => ex._src !== replacing) : (prev || [])).concat(tagged));
      showNotification?.(`${list.length} exigence(s) extraite(s) de « ${doc.nom} »`, 'success');
    } catch (e) { showNotification?.(`Erreur analyse du CdC : ${e.message}`, 'error'); }
    finally { setScanning(false); }
  };

  const replaceCdcDoc = (key) => { replaceRef.current = key; cdcInputRef.current?.click(); };
  const removeCdcDoc = (key) => {
    const doc = cdcDocs.find(d => d.key === key);
    setCdcDocs(prev => prev.filter(d => d.key !== key));
    setCdc(prev => {
      const rest = (prev || []).filter(ex => ex._src !== key);
      return rest.length || cdcDocs.length > 1 ? rest : null;
    });
    showNotification?.(`« ${doc?.nom || 'Document'} » retiré — ses exigences ne sont plus listées`, 'success');
  };

  const tracked = (nom) => docs.some(d => slug(d.nom).toLowerCase() === slug(nom).toLowerCase());
  const addToTracking = (ex) => {
    persist([...docs, { nom: ex.nom, source: ex.section || 'Cahier des charges', obligatoire: true, issued: null, validite_mois: /3\s*mois/i.test(ex.anciennete || '') ? 3 : /6\s*mois/i.test(ex.anciennete || '') ? 6 : 12 }]);
    showNotification?.(`« ${ex.nom} » ajoutée au suivi`, 'success');
  };

  // ── Bloc 3 : arborescence kDrive (dérivée du dossier réel) ──
  const buildTree = () => {
    const folders = report?.folders || [];
    const lots = folders.filter(f => f.bind !== 'candidate');
    const nodes = [{ depth: 0, name: '00_Administratif_societe', folder: true, state: 'new' }];
    docs.forEach(d => nodes.push({ depth: 1, name: `${slug(d.nom)}.pdf`, folder: false, state: 'new', missing: !d.issued }));
    lots.forEach((lot, i) => {
      const ref = (lot.path || '').split('/').pop();
      nodes.push({ depth: 0, name: `${String(i + 1).padStart(2, '0')}_Lot_${slug(ref)}`, folder: true, state: 'new' });
      folders.filter(f => f.bind === 'candidate' && (f.path || '').startsWith(`${lot.path}/`))
        .forEach(c => nodes.push({ depth: 1, name: candidateFolder((c.path || '').split('/').pop()), folder: true, state: 'new' }));
    });
    nodes.push({ depth: 0, name: '99_Annexes_client', folder: true, state: 'new' });
    return nodes;
  };

  const linkKdrive = async () => {
    setLinking(true);
    const root = `/kDrive/Appels d'offres/${slug(aoMeta?.titre || `AO_${aoId}`)}/`;
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/kdrive/link`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ root }) });
      if (r.status === 501) {
        showNotification?.('La liaison kDrive depuis cet écran arrive côté serveur — utilisez « Rescanner » avec l\'identifiant du dossier en attendant.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const d = j.data ?? j;
      setKdrive({ linked: true, root: d.root || root, folderId: d.folder_id ?? null, url: d.url || null });
      showNotification?.('Dossier kDrive lié — synchronisation en temps réel activée', 'success');
      loadEvents();
    } catch (e) { showNotification?.(`Erreur liaison kDrive : ${e.message}`, 'error'); }
    finally { setLinking(false); }
  };

  const createTree = async () => {
    setCreating(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/kdrive/tree`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ nodes: preview }) });
      if (r.status === 501) {
        showNotification?.('La création de l\'arborescence arrive côté serveur — rien n\'a été écrit dans kDrive.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 409 = dossier AO non lié à kDrive : on relaie le message du serveur.
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      // Idempotent : `created` ne liste que ce qui a réellement été écrit ; un
      // second appel renvoie une liste vide, les chips déjà « créé » le restent.
      const madeNames = new Set((j.created || []).map(c => c.name || (c.path || '').split('/').filter(Boolean).pop()));
      const madeUrl = new Map((j.created || []).filter(c => c.url).map(c => [c.name || (c.path || '').split('/').filter(Boolean).pop(), c.url]));
      setPreview(preview.map(n => (madeNames.has(n.name) || n.state === 'created')
        ? { ...n, state: 'created', url: madeUrl.get(n.name) || n.url }
        : n));
      setCreated(true);
      showNotification?.(`Arborescence créée dans kDrive — ${(j.created || []).length} élément(s)`, 'success');
      if (Array.isArray(j.events) && j.events.length) setEvents(j.events); else loadEvents();
    } catch (e) { showNotification?.(`Erreur création de l'arborescence : ${e.message}`, 'error'); }
    finally { setCreating(false); }
  };

  const toCreate = (preview || []).filter(n => n.folder && n.state !== 'created').length;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: '16px', alignItems: 'start' }}>
      {/* ───────── Bloc 1 — Suivi de validité ───────── */}
      <div style={{ ...card, gridColumn: '1', minWidth: 0 }}>
        <input ref={docInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; uploadSocieteDoc(f); }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '14px 16px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
          <span style={{ ...mono, ...chip('var(--code-bg)', 'var(--text-2)') }}><BI icon="lock-fill" style={{ marginRight: '5px' }} />Visible administrateur uniquement</span>
          <span style={{ flex: 1, minWidth: '200px', fontSize: '12.5px', color: 'var(--text-2)' }}>Pièces de la société — communes à tous les lots, jamais rattachées à un candidat</span>
          <span style={{ ...mono, fontSize: '12px', fontWeight: 700, color: conformes === docs.length ? '#16a34a' : 'var(--text-strong)' }}>{conformes}/{docs.length} conformes</span>
        </div>
        {storeUnavailable && (
          <div style={{ padding: '9px 16px', fontSize: '11.5px', color: '#d97706', background: 'rgba(217,119,6,0.08)', borderBottom: '1px solid var(--hair)' }}>
            <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />L'enregistrement de ces informations arrive côté serveur — vos modifications restent visibles jusqu'au rechargement de la page.
          </div>
        )}
        <div>
          {docs.map((d, i) => {
            const st = docState(d);
            const t = TONE[st.tone];
            return (
              <div key={`${d.nom}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '190px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{d.nom}</span>
                    <span style={{ ...mono, fontSize: '10px', fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d.obligatoire ? 'Obligatoire' : 'Optionnel'}</span>
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-2)', marginTop: '2px' }}>{d.source} · {st.sub}</div>
                </div>
                {/* Sélecteur de validité */}
                <div style={{ display: 'inline-flex', flex: 'none', borderRadius: '8px', overflow: 'hidden', background: 'var(--code-bg)' }}>
                  {[3, 6, 12, 0].map(m => (
                    <button key={m} onClick={() => setValidity(i, m)} title={m ? `Validité ${m} mois` : 'Sans échéance'}
                      style={{ ...mono, padding: '4px 9px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, background: Number(d.validite_mois) === m ? 'var(--accent)' : 'transparent', color: Number(d.validite_mois) === m ? 'var(--accent-fg)' : 'var(--text-3)' }}>
                      {m || 'sans'}
                    </button>
                  ))}
                </div>
                <span style={{ ...chip(t.bg, t.fg), flex: 'none' }}>{st.label}</span>
                <button onClick={() => askDocFile(i)} disabled={saving || uploading != null}
                  title={st.key === 'missing' ? 'Déposer le document dans 00_Administratif_societe' : 'Déposer la nouvelle version — l\'ancien fichier est remplacé'}
                  style={{ ...btn, flex: 'none', opacity: (saving || uploading != null) ? 0.6 : 1 }}>
                  <BI icon={uploading === i ? 'arrow-repeat' : (st.key === 'missing' ? 'cloud-arrow-up' : 'arrow-clockwise')} />
                  {uploading === i ? 'Dépôt…' : (st.key === 'missing' ? 'Importer' : 'Renouveler')}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───────── Bloc 2 — Scan du cahier des charges ───────── */}
      <div style={{ ...card, gridColumn: '2', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: '120px', fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>Cahier des charges</span>
          <input ref={cdcInputRef} type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; onCdcFile(f); }} />
          <button onClick={() => { replaceRef.current = null; cdcInputRef.current?.click(); }} disabled={!!scanning}
            title={cdcDocs.length ? 'Ajouter un autre document à analyser' : 'Importer le cahier des charges et l\'analyser'}
            style={{ ...btn, opacity: scanning ? 0.6 : 1 }}>
            <BI icon={cdcDocs.length ? 'plus-lg' : 'cloud-arrow-up'} /> {cdcDocs.length ? 'Ajouter un document' : 'Importer'}
          </button>
          <button onClick={() => scanCdc()} disabled={!!scanning} title="Analyser le cahier des charges déjà présent dans le dossier"
            style={{ ...btnMain, opacity: scanning ? 0.6 : 1 }}>
            <span style={{ ...mono, fontSize: '11px' }}>✦</span> {scanning ? 'Analyse…' : 'Scanner le CdC'}
          </button>
        </div>
        <div style={{ padding: '12px 16px' }}>
          {/* Documents importés : visibles, remplaçables, supprimables. */}
          {cdcDocs.map(d => (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 10px', marginBottom: '8px', border: '1px solid var(--input-border)', borderRadius: '10px', flexWrap: 'wrap' }}>
              <BI icon="file-earmark-text" style={{ flex: 'none', color: 'var(--text-3)' }} />
              <div style={{ flex: 1, minWidth: '120px' }}>
                <div style={{ ...mono, fontSize: '11.5px', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nom}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '2px' }}>
                  {d.count} exigence{d.count > 1 ? 's' : ''} extraite{d.count > 1 ? 's' : ''}{d.taille ? ` · ${fmtSize(d.taille)}` : ''}
                </div>
              </div>
              <span style={{ ...chip('rgba(45,200,110,0.15)', '#16a34a'), flex: 'none' }}><BI icon="check-lg" style={{ marginRight: '4px' }} />Analysé</span>
              <button onClick={() => replaceCdcDoc(d.key)} disabled={!!scanning} title="Remplacer ce document par une nouvelle version"
                style={{ ...btn, flex: 'none', padding: '6px 10px', opacity: scanning ? 0.6 : 1 }}><BI icon="arrow-clockwise" /> Remplacer</button>
              <button onClick={() => removeCdcDoc(d.key)} disabled={!!scanning} title="Retirer ce document et les exigences qu'il a apportées"
                style={{ ...btn, flex: 'none', padding: '6px 10px', color: 'var(--danger-fg)', borderColor: 'var(--danger-bd)', opacity: scanning ? 0.6 : 1 }}><BI icon="trash3" /> Retirer</button>
            </div>
          ))}

          {/* Traitement en cours : on montre l'étape, pas un écran figé. */}
          {scanning && (
            <div style={{ padding: '11px 12px', marginBottom: '8px', border: '1px solid var(--input-border)', borderRadius: '10px', background: 'var(--code-bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <BI icon="arrow-repeat" style={{ flex: 'none', color: 'var(--brand)', animation: 'ao-spin .9s linear infinite' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...mono, fontSize: '11.5px', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{scanning.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '2px' }}>
                    {scanning.phase === 'read' ? 'Lecture du document…' : 'Analyse par l\'IA — extraction des pièces exigées…'}
                  </div>
                </div>
              </div>
              <div style={{ height: '3px', marginTop: '9px', borderRadius: '2px', background: 'var(--hair)', overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, height: '100%', width: '40%', background: 'var(--brand)', borderRadius: '2px', animation: 'ao-recalc-bar 1.1s ease-in-out infinite' }} />
              </div>
            </div>
          )}

          {cdc == null ? (
            !scanning && <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>Importez le cahier des charges, ou lancez l'analyse s'il est déjà dans le dossier, pour en extraire les pièces exigées, leur section et l'ancienneté demandée.</div>
          ) : cdc.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>Aucune exigence détectée dans le cahier des charges.</div>
          ) : cdc.map((ex, i) => {
            const suivie = tracked(ex.nom);
            return (
              <div key={i} style={{ padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: '120px', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{ex.nom}</span>
                  <span style={chip(suivie ? 'rgba(45,200,110,0.15)' : 'rgba(217,119,6,0.14)', suivie ? '#16a34a' : '#d97706')}>{suivie ? 'Suivie' : 'Absente du suivi'}</span>
                </div>
                <div style={{ ...mono, fontSize: '11px', color: 'var(--text-2)', marginTop: '3px', wordBreak: 'break-word' }}>
                  {ex.section}{ex.anciennete ? ` · ${ex.anciennete}` : ''}
                </div>
                {!suivie && <button onClick={() => addToTracking(ex)} style={{ ...btn, marginTop: '7px' }}><BI icon="plus-lg" /> Ajouter au suivi</button>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ───────── Bloc 3 — Arborescence kDrive ───────── */}
      <div style={{ ...card, gridColumn: '1 / -1', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: '140px', fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>Arborescence kDrive</span>
          {kdrive.linked && !preview && (
            <button onClick={() => { if (cdc == null) { showNotification?.('Scanne d\'abord le cahier des charges — l\'arborescence en est déduite.', 'error'); return; } setPreview(buildTree()); setCreated(false); }}
              style={btnMain}><BI icon="diagram-3" /> Générer l'arborescence depuis le CdC</button>
          )}
        </div>

        {!kdrive.linked ? (
          <div style={{ margin: '16px', padding: '22px', textAlign: 'center', border: '1px dashed var(--input-border)', borderRadius: '12px' }}>
            <BI icon="folder-plus" style={{ fontSize: '22px', color: 'var(--text-faint)' }} />
            <div style={{ margin: '8px 0 12px', fontSize: '13px', color: 'var(--text-2)' }}>Aucun dossier kDrive lié</div>
            <button onClick={linkKdrive} disabled={linking} style={{ ...btnMain, opacity: linking ? 0.6 : 1 }}>
              <BI icon={linking ? 'arrow-repeat' : 'link-45deg'} /> {linking ? 'Liaison…' : 'Lier un dossier kDrive'}
            </button>
          </div>
        ) : (<>
          <div style={{ ...mono, display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', fontSize: '11.5px', color: 'var(--text-2)', background: 'var(--code-bg)', borderBottom: '1px solid var(--hair)' }}>
            <BI icon="hdd-network" style={{ flex: 'none' }} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kdrive.root}</span>
            <span style={{ flex: 'none', color: '#16a34a', fontWeight: 700 }}>temps réel</span>
          </div>
          {preview && (
            <div style={{ padding: '12px 16px' }}>
              {preview.map((n, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, padding: '4px 0', paddingLeft: `${n.depth * 20}px` }}>
                  <BI icon={n.folder ? 'folder' : 'file-earmark'} style={{ flex: 'none', fontSize: '12px', color: n.missing ? 'var(--danger-fg)' : 'var(--text-3)' }} />
                  <span style={{ ...mono, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px', color: n.missing ? 'var(--danger-fg)' : 'var(--text-strong)' }}>{n.name}</span>
                  {n.missing && <span style={{ ...chip('var(--danger-bg)', 'var(--danger-fg)'), flex: 'none' }}>absente</span>}
                  {n.folder && <span style={{ ...chip(n.state === 'created' ? 'rgba(45,200,110,0.15)' : 'rgba(217,119,6,0.14)', n.state === 'created' ? '#16a34a' : '#d97706'), flex: 'none' }}>{n.state === 'created' ? 'créé' : 'à créer'}</span>}
                </div>
              ))}
              {!created && (
                <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '11px', background: 'var(--code-bg)' }}>
                  <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                    <strong style={{ color: 'var(--text-strong)' }}>{toCreate} dossiers</strong> à créer — un par lot, un par candidat positionné, plus les pièces société. Rien n'est écrit dans kDrive avant ta validation.
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button onClick={createTree} disabled={creating} style={{ ...btnMain, opacity: creating ? 0.6 : 1 }}>
                      <BI icon={creating ? 'arrow-repeat' : 'check-lg'} /> {creating ? 'Création…' : 'Valider et créer'}
                    </button>
                    <button onClick={() => setPreview(null)} style={btn}>Annuler</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>)}

        {/* ── Bloc 4 — Contrat de synchronisation ── */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--hair)' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '8px' }}>Règles de synchronisation</div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.65 }}>
            <li><strong style={{ color: 'var(--text-strong)' }}>Racine par dossier</strong> — un dossier kDrive par AO, créé à la liaison, jamais renommé ensuite.</li>
            <li><strong style={{ color: 'var(--text-strong)' }}>Un dossier par lot</strong> — <span style={mono}>NN_Lot_&#123;référence&#125;</span>, l'ordre suit celui des lots du cahier des charges.</li>
            <li><strong style={{ color: 'var(--text-strong)' }}>Un sous-dossier par candidat</strong> — <span style={mono}>NOM_Prénom</span>, créé automatiquement quand le candidat est positionné sur le lot depuis le besoin.</li>
            <li><strong style={{ color: 'var(--text-strong)' }}>Pièces société hors lots</strong> — <span style={mono}>00_Administratif_societe</span> est partagé : une seule copie, liée au suivi de validité (renouvellement = remplacement du fichier).</li>
            <li><strong style={{ color: 'var(--text-strong)' }}>Retrait d'un candidat</strong> — son dossier est supprimé de kDrive : l'arborescence ne garde que les profils réellement déposés.</li>
          </ul>
          {events.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '6px' }}>Journal kDrive</div>
              {events.slice(0, 3).map((e, i) => (
                <div key={i} style={{ ...mono, fontSize: '11px', color: 'var(--text-2)', padding: '3px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {typeof e === 'string' ? e : `${e.type || ''} — ${e.message || ''}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
